<template>
  <div>
    <Toolbar
      v-if="show_toolbar"
      :moduleName="moduleName"
      :sortableHeaders="sortableHeaders"
      :config="config"
      :onCreate="onCreate"
      :title="title"
      :availableViews="availableViews"
    />

    <component
      :is="currentViewComponent"
      :moduleName="moduleName"
      :headers="headers"
      :data="data"
      :config="config"
      :onUpdate="onUpdate"
      :onRecordUpdate="onRecordUpdate"
      :onDelete="onDelete"
      @viewItem="openDetailDialog"
      @editItem="openEditDialog"
      :title="title"
      :statusField="statusField"
    />

    <v-dialog
      v-model="showDialog"
      fullscreen
      persistent
    >
      <component
        v-if="detailComponent && selectedId"
        :is="detailComponent"
        :id="selectedId"
        @close="closeDialog"
        :onUpdate="onUpdate"
      />
    </v-dialog>

    <Pagination v-if="show_pagination" :moduleName="moduleName" :title="title"/>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePreferencesStore } from '@/store/preferences';
import { useLayoutField } from '@/composables/useLayoutField';
import { useHydratedHeaders } from '@/composables/useHydratedHeaders';
import Toolbar from '@/components/layout/Toolbar.vue';
import Pagination from '@/components/layout/Pagination.vue';
import { configRegistry } from '@/lib/configRegistry';

const props = defineProps<{
  moduleName: string;
  listViewComponent: any;
  calendarViewComponent: any;
  gridViewComponent: any;
  cardViewComponent: any;
  filterComponent?: any;
  data: any;
  config: string;
  onCreate: (data: any) => Promise<any>;
  onUpdate: (id: number, data: any) => Promise<any>;
  onRecordUpdate?: (id: number, data: any) => Promise<any>;
  onDelete?: (id: number) => Promise<any>;
  show_toolbar: boolean;
  show_pagination: boolean;
  title: any;
}>();

const preferencesStore = usePreferencesStore();
const route = useRoute();
const router = useRouter();
const statusField = ref<string>('');


const currentViewComponent = computed(() => {
  switch (useLayoutField(props.moduleName, 'currentView').value) {
    case 'table':
      return props.listViewComponent;
    case 'grid':
      return props.gridViewComponent;
    case 'card':
      return props.cardViewComponent;
      case 'calendar':
      return props.calendarViewComponent;
    default:
      return props.listViewComponent;
  }
});

const availableViews = computed(() => {
  const views = [];
  if (props.listViewComponent) views.push('table');
  if (props.gridViewComponent) views.push('grid');
  if (props.cardViewComponent) views.push('card');
  if (props.calendarViewComponent) views.push('calendar');
  return views;
});

const headers = useHydratedHeaders();
const sortableHeaders = computed(() =>
  preferencesStore.allModuleHeaders.filter((h) => h.sortable)
);

const showDialog = ref(false);
const selectedId = ref<number | null>(null);
const detailComponent = ref<any>(null);

// 🚀 Carga el componente DETAIL dinámicamente si no existe
const loadDetailComponent = async () => {
  const loader = configRegistry[props.moduleName];
  if (!loader) {
    console.warn(`No se encontró config para módulo: ${props.moduleName}`);
    return;
  }
  const mod = await loader();
  if (mod?.DETAIL?.component) {
    detailComponent.value = await mod.DETAIL.component;
  } else {
    console.warn(`[${props.moduleName}] No se definió DETAIL.component`);
  }
};

// 👀 Verifica si hay un `id` en la URL (por acceso directo o navegación interna)
const syncDialogWithRoute = async () => {
  const id = route.params.id;
  if (id) {
    selectedId.value = +id;
    await loadDetailComponent();
    showDialog.value = true;
  }
};

// 🧠 Observa cambios de ID en la URL y sincroniza el diálogo
watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await syncDialogWithRoute();
    }
    if (!newId && showDialog.value) {
      showDialog.value = false;
      selectedId.value = null;
    }
  },
  { immediate: true }
);

// 🟢 Abrir el diálogo desde acción del usuario
const openDetailDialog = async (item: any) => {
  selectedId.value = item?.id ?? item;
  await loadDetailComponent();
  showDialog.value = true;

  router.push({
    name: route.name as string,
    params: { ...route.params, id: selectedId.value },
    query: route.query,
  });
};

// 🔴 Cerrar el diálogo y limpiar el param `id` de la URL
const closeDialog = () => {
  showDialog.value = false;
  selectedId.value = null;

  const { id, ...otherParams } = route.params;

  router.replace({
    name: route.name as string,
    params: { ...otherParams },
    query: route.query,
  });
};

onMounted(async () => {
    const loader = configRegistry[props.moduleName];
    if (loader) {
      const mod = await loader();
      statusField.value = mod?.KANBAN?.statusField ?? 'status'; // fallback genérico
    }
  });
</script>

<script lang="ts">
export default {};
</script>